/*
 * Copyright (c) 2026 Ambits Technologies LLC. All rights reserved.
 *
 * This source code is the proprietary and confidential property of
 * Ambits Technologies LLC. Unauthorized copying, modification, distribution,
 * or use of this software, via any medium, is strictly prohibited.
 *
 * MyAmbits - AI-Powered Location Safety & Social Platform
 * https://myambits.com
 */

/*
 * LoggedOutFlow — the entry experience for a signed-out visitor.
 *
 * Welcome (Build 130 carousel, untouched) → notifications primer → phone entry
 * / OTP. "Get started" first shows the notification primer (so we explain push
 * value and surface the iOS permission dialog at a natural moment, before the
 * user is signed in), then advances to phone verification — the PRIMARY sign-in
 * (AuthContext.verifyPhone → signInWithCredential). Once verified, AuthContext
 * flips `user` non-null and App.tsx swaps this whole subtree for the authed
 * route tree, so this component never handles the post-sign-in transition.
 *
 * The primer only requests device-level permission; the FCM token is captured
 * post-auth (useFCMTokenCapture) once a uid exists.
 */

import { useState } from "react";
import WelcomeScreen from "@/components/Auth/WelcomeScreen";
import NotificationPrimerScreen from "@/components/onboarding/NotificationPrimerScreen";
import PhoneVerificationSheet from "@/components/onboarding/PhoneVerificationSheet";
import { getNotificationPermissionState } from "@/utils/notificationPermission";

type Stage = "welcome" | "notifications" | "phone";

export default function LoggedOutFlow() {
  const [stage, setStage] = useState<Stage>("welcome");

  if (stage === "welcome") {
    return (
      <WelcomeScreen
        onGetStarted={async () => {
          // Only show the "Stay in the loop" primer until notifications are
          // actually GRANTED. Once granted it never appears again; while still
          // denied/undecided it keeps offering the turn-on on every login.
          const perm = await getNotificationPermissionState();
          setStage(perm === "granted" ? "phone" : "notifications");
        }}
      />
    );
  }

  if (stage === "notifications") {
    // Pre-permission primer. Both "Turn on" (after the iOS dialog) and "Not
    // now" advance to phone entry — onboarding is never blocked here. Back
    // returns to Welcome.
    return (
      <NotificationPrimerScreen
        onContinue={() => setStage("phone")}
        onBack={() => setStage("welcome")}
      />
    );
  }

  // Phone entry / OTP. `onExit` returns to Welcome — a non-destructive back
  // out of the verification flow (no user exists yet to log out).
  return <PhoneVerificationSheet onExit={() => setStage("welcome")} />;
}
